import { useEffect, useRef, useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Plus, Trash2, Wand2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TitleCaseInput } from "@/components/ui/text-input";
import {
  redCreateSchema, type RedCreateInput, type RedDetail,
} from "./schemas";
import { useAddIPReservada, useCreateRed, useDeleteIPReservada, useUpdateRed } from "./api";
import { useListRouters } from "@/features/routers/api";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import type { ApiError } from "@/lib/api";
import { defaultsFromCidr, parseCidr } from "@/lib/cidr";

const DEFAULTS: RedCreateInput = {
  nombre: "",
  descripcion: "",
  cidr: "",
  gateway: "",
  dns_primario: "8.8.8.8",
  dns_secundario: "1.1.1.1",
  vlan_id: "",
  pool_start: "",
  pool_end: "",
  uso_interno: false,
  router_id: "",
  tags: [],
};

function toForm(r: RedDetail): RedCreateInput {
  return {
    nombre: r.nombre,
    descripcion: r.descripcion ?? "",
    cidr: r.cidr,
    gateway: r.gateway ?? "",
    dns_primario: r.dns_primario ?? "",
    dns_secundario: r.dns_secundario ?? "",
    vlan_id: r.vlan_id ?? "",
    pool_start: r.pool_start ?? "",
    pool_end: r.pool_end ?? "",
    uso_interno: r.uso_interno,
    router_id: r.router_id ?? "",
    tags: r.tags ?? [],
  };
}

function clean<T>(v: T): T {
  if (typeof v === "string") return (v.trim() === "" ? undefined : v.trim()) as T;
  if (Array.isArray(v)) return v.map(clean) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      const c = clean(val);
      if (c !== undefined) out[k] = c;
    }
    return out as T;
  }
  return v;
}

export function RedFormDialog({
  open, onOpenChange, red, onSaved,
}: { open: boolean; onOpenChange: (v: boolean) => void; red?: RedDetail | null; onSaved?: (id: string) => void }) {
  const editMode = !!red;
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState("config");
  const [newIP, setNewIP] = useState({ ip: "", descripcion: "" });

  const { register, handleSubmit, control, reset, watch, setValue, getValues, formState: { errors } } = useForm<RedCreateInput>({
    resolver: zodResolver(redCreateSchema),
    defaultValues: DEFAULTS,
  });

  const cidrValue = watch("cidr");
  const cidrInfo = cidrValue ? parseCidr(cidrValue) : null;

  /**
   * Auto-fill gateway/pool SIEMPRE que el CIDR cambia a uno válido.
   * Sobrescribe valores previos (porque cambió la red → los viejos ya no aplican).
   * Se desactiva en edit mode (CIDR está lockeado).
   * Uso ref para trackear el último CIDR procesado y no regenerar en renders.
   */
  const lastAppliedCidr = useRef<string>("");
  useEffect(() => {
    if (editMode || !cidrValue || cidrValue === lastAppliedCidr.current) return;
    const defs = defaultsFromCidr(cidrValue);
    if (!defs) return;
    lastAppliedCidr.current = cidrValue;
    setValue("gateway", defs.gateway, { shouldDirty: true });
    setValue("pool_start", defs.pool_start, { shouldDirty: true });
    setValue("pool_end", defs.pool_end, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cidrValue, editMode]);

  /** Botón explícito "Regenerar" para forzar recalc (útil si el user cambió los defaults a mano). */
  function regenerateDefaults() {
    if (!cidrValue) return;
    const defs = defaultsFromCidr(cidrValue);
    if (!defs) return;
    setValue("gateway", defs.gateway, { shouldDirty: true });
    setValue("pool_start", defs.pool_start, { shouldDirty: true });
    setValue("pool_end", defs.pool_end, { shouldDirty: true });
  }

  useEffect(() => {
    if (open) { reset(red ? toForm(red) : DEFAULTS); setError(null); setTab("config"); setNewIP({ ip: "", descripcion: "" }); }
  }, [open, red, reset]);

  const createMut = useCreateRed();
  const updateMut = useUpdateRed(red?.id ?? "");
  const addIpMut = useAddIPReservada(red?.id ?? "");
  const delIpMut = useDeleteIPReservada(red?.id ?? "");
  const { data: routersData } = useListRouters({ page: 1, size: 100 });

  const onSubmit: SubmitHandler<RedCreateInput> = async (values) => {
    setError(null);
    try {
      const cleaned = clean(values);
      const saved = editMode
        ? await updateMut.mutateAsync(cleaned)
        : await createMut.mutateAsync(cleaned);
      onSaved?.(saved.id);
      onOpenChange(false);
    } catch (e) { setError((e as ApiError).message ?? "No se pudo guardar"); }
  };

  async function handleAddIP() {
    if (!newIP.ip || !red) return;
    try {
      await addIpMut.mutateAsync({ ip: newIP.ip, descripcion: newIP.descripcion });
      setNewIP({ ip: "", descripcion: "" });
    } catch (e) { alert(`Error: ${(e as ApiError).message}`); }
  }

  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle>{editMode ? "Editar red" : "Nueva red"}</DialogTitle>
          <DialogDescription>Subred IPv4 con pool asignable para contratos.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="config">Configuración</TabsTrigger>
              <TabsTrigger value="ips" disabled={!editMode}>IPs reservadas {red && `(${red.ips_reservadas.length})`}</TabsTrigger>
            </TabsList>

            <TabsContent value="config">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Nombre *</Label>
                  <TitleCaseInput control={control} name="nombre" placeholder="Red Centro 192.168.100.0/24" />
                  {errors.nombre && <span className="text-xs text-destructive">{errors.nombre.message}</span>}
                </div>
                <div>
                  <Label className="flex items-center justify-between gap-2">
                    <span>CIDR *</span>
                    {!editMode && cidrInfo && (
                      <button
                        type="button"
                        onClick={regenerateDefaults}
                        className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                        title="Regenerar gateway + pool desde el CIDR"
                      >
                        <Wand2 size={10} /> regenerar auto
                      </button>
                    )}
                  </Label>
                  <Input {...register("cidr")} placeholder="192.168.100.0/24" disabled={editMode} />
                  {errors.cidr && <span className="text-xs text-destructive">{errors.cidr.message}</span>}
                  {editMode ? (
                    <span className="text-[10px] text-muted-foreground">El CIDR no se puede cambiar post-creación.</span>
                  ) : cidrInfo ? (
                    <span className="text-[10px] text-muted-foreground">
                      Red <span className="font-mono">{cidrInfo.network}</span> · Broadcast{" "}
                      <span className="font-mono">{cidrInfo.broadcast}</span> · {cidrInfo.usable} hosts asignables
                    </span>
                  ) : null}
                </div>
                <div>
                  <Label>Router que rutea *</Label>
                  <Combobox
                    options={
                      (routersData?.items ?? []).map((r): ComboboxOption => ({
                        value: r.id,
                        label: r.nombre,
                        hint: r.ip_address,
                      }))
                    }
                    value={(getValues("router_id") as string) ?? ""}
                    onChange={(v) => setValue("router_id", v, { shouldDirty: true, shouldValidate: true })}
                    placeholder="Buscar router…"
                  />
                  <span className="text-[10px] text-muted-foreground">
                    Al crear contratos, solo se mostrarán redes del mismo router.
                  </span>
                  {errors.router_id && <span className="text-xs text-destructive">{errors.router_id.message}</span>}
                </div>
                <div>
                  <Label>Gateway</Label>
                  <Input {...register("gateway")} placeholder="192.168.100.1" />
                </div>
                <div>
                  <Label>DNS primario</Label>
                  <Input {...register("dns_primario")} placeholder="8.8.8.8" />
                </div>
                <div>
                  <Label>DNS secundario</Label>
                  <Input {...register("dns_secundario")} placeholder="1.1.1.1" />
                </div>
                <div>
                  <Label>Pool start</Label>
                  <Input {...register("pool_start")} placeholder="192.168.100.2" />
                </div>
                <div>
                  <Label>Pool end</Label>
                  <Input {...register("pool_end")} placeholder="192.168.100.254" />
                </div>
                <div className="col-span-2">
                  <Label>Descripción</Label>
                  <Textarea rows={2} {...register("descripcion")} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="ips">
              {!red ? null : (
                <div className="flex flex-col gap-3">
                  <div className="flex items-end gap-2 rounded-md border bg-muted/30 p-2">
                    <div className="flex-1">
                      <Label>IP</Label>
                      <Input
                        value={newIP.ip}
                        onChange={(e) => setNewIP((s) => ({ ...s, ip: e.target.value }))}
                        placeholder="192.168.100.5"
                      />
                    </div>
                    <div className="flex-[2]">
                      <Label>Descripción</Label>
                      <Input
                        value={newIP.descripcion}
                        onChange={(e) => setNewIP((s) => ({ ...s, descripcion: e.target.value }))}
                        placeholder="Gateway OSPF, printer, etc."
                      />
                    </div>
                    <Button type="button" size="sm" onClick={handleAddIP} disabled={!newIP.ip || addIpMut.isPending}>
                      <Plus size={12} /> Reservar
                    </Button>
                  </div>

                  {red.ips_reservadas.length === 0 ? (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                      No hay IPs reservadas en esta red.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {red.ips_reservadas.map((ip) => (
                        <div key={ip.id} className="flex items-center gap-2 rounded-md border px-2 py-1 text-xs">
                          <span className="font-mono flex-1">{ip.ip}</span>
                          <span className="text-muted-foreground flex-[2] truncate">{ip.descripcion || "—"}</span>
                          <button
                            type="button"
                            onClick={() => delIpMut.mutate(ip.id)}
                            className="rounded p-1 text-destructive hover:bg-destructive/10"
                            title="Liberar"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Guardando…" : editMode ? "Guardar cambios" : "Crear red"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
